import { z } from 'zod';

/**
 * Widgets: small views of a plugin's (or the core's) data that the core renders itself, the same
 * everywhere: as cards in conversations, on dashboards, when an agent shows one. A widget's kind
 * says how its data is drawn; `view` is the escape hatch, one of the plugin's views in a frame,
 * for what data cannot say (a form to answer, a preview).
 */
export const WIDGET_DATA_KINDS = ['stat', 'line', 'bar', 'table', 'list'] as const;
export const WIDGET_KINDS = [...WIDGET_DATA_KINDS, 'view'] as const;
export type WidgetDataKind = (typeof WIDGET_DATA_KINDS)[number];
export type WidgetKind = (typeof WIDGET_KINDS)[number];

/** The core's widgets are `crowdex/<id>`, as a plugin's are `<plugin>/<id>`. */
export const CORE_WIDGET_PUBLISHER = 'crowdex';

const widgetIdSchema = z.string().regex(/^[a-z0-9][a-z0-9_-]{0,63}$/);
/** `<plugin or crowdex>/<id>`. */
export const widgetNameSchema = z
  .string()
  .max(192)
  .regex(/^[a-z0-9][a-z0-9._-]*\/[a-z0-9][a-z0-9_-]{0,63}$/, 'Widgets are "<plugin>/<id>".');

// --- data ------------------------------------------------------------------------------------

export const widgetCellSchema = z.union([z.string().max(4096), z.number(), z.boolean(), z.null()]);
export type WidgetCell = z.infer<typeof widgetCellSchema>;

export const widgetSeriesRowSchema = z
  .object({
    /** The period's start (ISO), for a series over time. */
    at: z.string().max(64).optional(),
    /** The group's name, for a series per group. */
    group: z.string().max(256).optional(),
    value: z.number(),
  })
  .strict();
export type WidgetSeriesRow = z.infer<typeof widgetSeriesRowSchema>;

/** What a widget shows: numbers (stats and charts) or records (tables and lists). */
export const widgetDataSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('series'),
      rows: z.array(widgetSeriesRowSchema).max(5000),
      /** The currency of its values (ISO code). */
      currency: z
        .string()
        .regex(/^[A-Z]{3}$/)
        .optional(),
      /** A stat's value over the range before, to compare. */
      previous: z.number().optional(),
      /** A stat's values per period over its range, for its trend. */
      trend: z.array(z.number()).max(400).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('records'),
      columns: z.array(z.string().max(128)).max(12),
      records: z.array(z.record(z.string(), widgetCellSchema)).max(500),
      /** How many records there were before the first ones were kept. */
      total: z.number().int().nonnegative(),
    })
    .strict(),
]);
export type WidgetData = z.infer<typeof widgetDataSchema>;

/** A service the user has to install or connect before a widget can show anything. */
export const widgetWaitingSchema = z
  .object({ service: z.string().min(1).max(128), name: z.string().min(1).max(128) })
  .strict();

/** What a plugin's data route answers: the data, or the service it waits for. */
export const widgetDataAnswerSchema = z.union([
  widgetDataSchema,
  z.object({ waiting: widgetWaitingSchema }).strict(),
]);

/** A widget's data as the core hands it on: shown, waiting for a service, or failed. */
export const widgetResultSchema = z.union([
  z.object({ ok: z.literal(true), data: widgetDataSchema, fetchedAt: z.string() }).strict(),
  z.object({ ok: z.literal(false), waiting: widgetWaitingSchema }).strict(),
  z.object({ ok: z.literal(false), error: z.string() }).strict(),
]);
export type WidgetResult = z.infer<typeof widgetResultSchema>;

// --- declarations ----------------------------------------------------------------------------

/** A parameter of a widget, filled where it is placed (a repository, a range, a run). */
export const widgetParamSchema = z
  .object({
    type: z.enum(['string', 'number', 'boolean', 'json']),
    title: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    required: z.boolean().default(false),
    enum: z.array(z.string().max(128)).min(1).max(64).optional(),
  })
  .strict();
export type WidgetParam = z.infer<typeof widgetParamSchema>;

const appRoute = z
  .string()
  .max(512)
  .regex(/^\/[A-Za-z0-9\-._~!$&'()*+,;=:@%/]*$/, 'Routes are absolute URL paths.')
  .refine((path) => !path.split('/').includes('..'), 'Routes must not contain "..".');

/** How a widget's values read: their format, which change is good, how wide it is on a grid. */
const presentation = {
  /** `duration`: seconds, read as "4 min 12". */
  format: z.enum(['number', 'currency', 'percent', 'duration']).optional(),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional(),
  /** Stats: which way of change is good (green), `none` when neither is. */
  better: z.enum(['up', 'down', 'none']).optional(),
  /** Columns of a dashboard's 4-column grid it spans. */
  size: z.enum(['small', 'medium', 'large', 'full']).optional(),
  /** Lists: the field holding each record's link. */
  link: z.string().max(128).optional(),
};

/**
 * A widget a plugin provides (`widgets` of its manifest). A data widget's `data` route answers
 * `POST { params }` with its data (`widgetDataSchema`); a `view` widget is its route, framed with
 * its params as the view's input. A parameter named `range` (`7d`, `30d`, `90d`…) is the one a
 * dashboard's range picker changes.
 */
export const widgetDeclarationSchema = z
  .object({
    id: widgetIdSchema,
    title: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    kind: z.enum(WIDGET_KINDS),
    data: appRoute.optional(),
    view: appRoute.optional(),
    params: z
      .record(z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,31}$/), widgetParamSchema)
      .optional(),
    ...presentation,
  })
  .strict()
  .refine((widget) => (widget.kind === 'view') === (widget.view !== undefined), {
    message: 'A view widget names its view, and only it does.',
    path: ['view'],
  })
  .refine((widget) => (widget.kind === 'view') !== (widget.data !== undefined), {
    message: 'A data widget names its data route.',
    path: ['data'],
  })
  .refine((widget) => Object.keys(widget.params ?? {}).length <= 16, 'At most 16 params.');
export type WidgetDeclaration = z.infer<typeof widgetDeclarationSchema>;

/** A widget as the core lists it: its declaration under its full name, and who provides it. */
export type ListedWidget = Omit<WidgetDeclaration, 'id'> & {
  widget: string;
  /** The plugin's title, or Crowdex. */
  provider: string;
};

// --- placed widgets --------------------------------------------------------------------------

export const widgetParamValueSchema = z.union([
  z.string().max(4096),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.unknown()).max(64),
  z.record(z.string(), z.unknown()),
]);

/**
 * A widget where it is placed (a conversation's card, a dashboard's tile): which one, its
 * params, and how this place shows it (another data kind, a title of its own).
 */
export const widgetRefSchema = z
  .object({
    widget: widgetNameSchema,
    params: z.record(z.string().max(32), widgetParamValueSchema).default({}),
    /** Another data kind than the declared one (a metric as a line rather than a stat). */
    kind: z.enum(WIDGET_DATA_KINDS).optional(),
    title: z.string().min(1).max(200).optional(),
    description: z.string().max(400).optional(),
    ...presentation,
  })
  .strict();
export type WidgetRef = z.input<typeof widgetRefSchema>;
export type ParsedWidgetRef = z.output<typeof widgetRefSchema>;

/**
 * `widgets.data`: a placed widget's data, from the core or its plugin, for the kind it is shown
 * as. A plugin's data route gets `{ params, kind }`.
 */
export const widgetDataInputSchema = z
  .object({
    widget: widgetNameSchema,
    params: z.record(z.string().max(32), widgetParamValueSchema).default({}),
    kind: z.enum(WIDGET_DATA_KINDS).optional(),
  })
  .strict();
export type WidgetDataInput = z.input<typeof widgetDataInputSchema>;

/** Splits `<plugin>/<id>`. */
export function widgetNameParts(name: string): { publisher: string; id: string } {
  const slash = name.lastIndexOf('/');
  return { publisher: name.slice(0, slash), id: name.slice(slash + 1) };
}

// --- periods ---------------------------------------------------------------------------------

/** The start of a range back from `now`: `7d`, `12w`, `6m`, `1y`. */
export function rangeStart(range: string, now: Date): Date {
  const count = Number(range.slice(0, -1));
  const start = new Date(now);
  switch (range.at(-1)) {
    case 'd':
      start.setUTCDate(start.getUTCDate() - count);
      break;
    case 'w':
      start.setUTCDate(start.getUTCDate() - 7 * count);
      break;
    case 'm':
      start.setUTCMonth(start.getUTCMonth() - count);
      break;
    default:
      start.setUTCFullYear(start.getUTCFullYear() - count);
  }
  return start;
}

export const RANGE_PATTERN = /^[1-9][0-9]{0,3}[dwmy]$/;

/** The period a range's trend is drawn in: days up to a month, weeks up to half a year. */
export function trendInterval(range: string, now: Date): 'day' | 'week' | 'month' {
  const days = (now.getTime() - rangeStart(range, now).getTime()) / 864e5;
  return days <= 31 ? 'day' : days <= 183 ? 'week' : 'month';
}

/** The start of a date's period, in UTC (weeks start on Monday, as PostgreSQL's). */
export function periodStart(date: Date, interval: 'day' | 'week' | 'month'): string {
  const start = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  if (interval === 'week') start.setUTCDate(start.getUTCDate() - ((start.getUTCDay() + 6) % 7));
  if (interval === 'month') start.setUTCDate(1);
  return start.toISOString();
}

/**
 * A series with every period of its range, those without values at 0 (a line through them, an
 * empty column), from `from` (else its first period) to `to`.
 */
export function fillPeriods(
  rows: WidgetSeriesRow[],
  interval: 'day' | 'week' | 'month',
  to: Date,
  from?: Date,
): WidgetSeriesRow[] {
  const present = new Set(rows.map((row) => row.at));
  const first =
    from === undefined
      ? rows.map((row) => row.at ?? '').toSorted()[0]
      : periodStart(from, interval);
  if (first === undefined || first === '') return rows;
  const group = rows.find((row) => row.group !== undefined)?.group;
  const filled = [...rows];
  const last = periodStart(to, interval);
  for (let at = new Date(first); at.toISOString() <= last; ) {
    const key = at.toISOString();
    if (!present.has(key))
      filled.push({ at: key, ...(group === undefined ? {} : { group }), value: 0 });
    if (interval === 'day') at.setUTCDate(at.getUTCDate() + 1);
    else if (interval === 'week') at.setUTCDate(at.getUTCDate() + 7);
    else at.setUTCMonth(at.getUTCMonth() + 1);
  }
  return filled.toSorted(
    (left, right) =>
      (left.at ?? '').localeCompare(right.at ?? '') ||
      (left.group ?? '').localeCompare(right.group ?? ''),
  );
}
